function resolveSearchBaseUrl() {
  const raw = window.FoxTheme?.routes?.search_url;
  if (raw && typeof raw === "string") {
    try {
      const u = new URL(raw, window.location.origin);
      return `${u.origin}${u.pathname}`;
    } catch (_) {}
  }
  return `${window.location.origin}/search`;
}

function isShopifySearchTemplatePath() {
  const raw = window.FoxTheme?.routes?.search_url;
  if (raw && typeof raw === "string") {
    try {
      const want = new URL(raw, window.location.origin).pathname.replace(/\/$/, "") || "/";
      const cur = window.location.pathname.replace(/\/$/, "") || "/";
      return cur === want;
    } catch (_) {}
  }
  return /^\/search\/?$/.test(window.location.pathname);
}

const SEARCH_URL = () => resolveSearchBaseUrl();

const COVEO_SEARCH_INPUT_DEBOUNCE_MS = 300;

function wireSearchBoxWithSuggestions(engine, config) {
  const { input, form, suggestionsEl, recentQueriesEl, wrapper } = config;
  if (!input || !form) return null;

  const searchUrl = SEARCH_URL();
  const buildStandaloneSearchBox =
    config.buildStandaloneSearchBox ||
    (typeof config.commerceModule?.buildStandaloneSearchBox === "function"
      ? config.commerceModule.buildStandaloneSearchBox
      : config.commerceModule?.standaloneSearchBox);

  if (!buildStandaloneSearchBox) return null;

  const opts = {
    redirectionUrl: searchUrl,
    overwrite: true,
    numberOfSuggestions: 5,
  };
  if (config.boxId) opts.id = config.boxId;
  const standaloneSearchBox = buildStandaloneSearchBox(engine, {
    options: opts,
  });

  const updateText =
    typeof standaloneSearchBox.updateText === "function"
      ? standaloneSearchBox.updateText.bind(standaloneSearchBox)
      : null;
  const selectSuggestion =
    typeof standaloneSearchBox.selectSuggestion === "function"
      ? standaloneSearchBox.selectSuggestion.bind(standaloneSearchBox)
      : null;

  const navigateToSearch = (q) => {
    const targetUrl = q ? searchUrl + "?q=" + encodeURIComponent(q) : searchUrl;
    window.location.href = targetUrl;
  };

  /**
   * Cause of the most recent navigation toward the search page.
   *  - "querySuggestion": user picked a suggestion from the dropdown.
   *  - "submit": user typed a query and pressed Enter / Search.
   * The search page reads this from localStorage and routes the query
   * through `searchBox.selectSuggestion(...)` (vs `updateQuery(...)`)
   * so the Coveo "Search Suggestion Query" analytics model receives the
   * proper click event.
   */
  let lastSubmitCause = "submit";

  let pendingRedirect = false;
  let inputDebounceTimer = null;

  const flushDebouncedInput = () => {
    if (inputDebounceTimer != null) {
      clearTimeout(inputDebounceTimer);
      inputDebounceTimer = null;
    }
  };

  const isInputActivelyEditing = () =>
    document.activeElement === input;

  const hideSuggestions = () => {
    if (wrapper) wrapper.classList.remove("coveo-suggestions-visible");
    if (suggestionsEl) {
      suggestionsEl.classList.add("hidden");
      suggestionsEl.innerHTML = "";
    }
    if (recentQueriesEl) recentQueriesEl.classList.add("hidden");
  };

  if (typeof standaloneSearchBox.subscribe === "function") {
    standaloneSearchBox.subscribe(() => {
      const state = standaloneSearchBox.state || {};
      if (state.redirectTo) {
        pendingRedirect = true;
        hideSuggestions();
        const data = JSON.stringify({
          value: state.value,
          analytics: state.analytics,
          // Coveo Delivery Assurance fix: forward the cause so the search page
          // can fire the proper "Search Suggestion Query" click analytics
          // event when the query came from a suggestion pick.
          cause: lastSubmitCause,
        });
        try {
          localStorage.setItem("coveo_standalone_search_box_data", data);
        } catch (_) {}
        const targetUrl = new URL(state.redirectTo, window.location.origin);
        if (state.value && !targetUrl.searchParams.has("q")) {
          targetUrl.searchParams.set("q", state.value);
        }
        window.location.href = targetUrl.toString();
        return;
      }

      if (pendingRedirect) return;

      const value = state.value ?? input.value ?? "";
      /*
       * Do not push the controller value back into the DOM while the user is
       * actively typing. The standalone search box can emit a slightly stale
       * `state.value` during debounced updates, which makes the hero search
       * field appear to "drop" a character on the homepage.
       */
      if (!isInputActivelyEditing() && value !== input.value) {
        input.value = value;
      }

      function toSuggestionList(s) {
        if (!s) return [];
        if (Array.isArray(s)) return s;
        if (Array.isArray(s.suggestions)) return s.suggestions;
        return [];
      }
      const list =
        toSuggestionList(state.suggestions).length > 0
          ? toSuggestionList(state.suggestions)
          : toSuggestionList(state.querySuggest).length > 0
            ? toSuggestionList(state.querySuggest)
            : toSuggestionList(state.querySuggestions);

      if (value.trim() && list.length > 0) {
        renderSuggestions(suggestionsEl, list, (val) => {
          flushDebouncedInput();
          pendingRedirect = true;
          lastSubmitCause = "querySuggestion";
          hideSuggestions();
          if (selectSuggestion) {
            selectSuggestion(val);
          } else if (updateText) {
            updateText(val);
            navigateToSearch(val);
          } else {
            navigateToSearch(val);
          }
        });
        if (suggestionsEl) {
          suggestionsEl.classList.remove("hidden");
          suggestionsEl.style.display = "block";
          if (wrapper) wrapper.classList.add("coveo-suggestions-visible");
        }
        if (recentQueriesEl) recentQueriesEl.classList.add("hidden");
      } else {
        if (wrapper) wrapper.classList.remove("coveo-suggestions-visible");
        if (suggestionsEl) suggestionsEl.classList.add("hidden");
        if (recentQueriesEl) recentQueriesEl.classList.add("hidden");
      }
    });
  }

  input.addEventListener("input", (e) => {
    const v = e.target.value;
    if (!updateText) return;
    flushDebouncedInput();
    inputDebounceTimer = setTimeout(() => {
      inputDebounceTimer = null;
      updateText(v);
    }, COVEO_SEARCH_INPUT_DEBOUNCE_MS);
  });
  input.addEventListener("focus", () => {
    const v = input.value.trim();
    flushDebouncedInput();
    if (v && updateText) updateText(v);
    else if (v && typeof standaloneSearchBox.showSuggestions === "function") {
      standaloneSearchBox.showSuggestions();
    }
  });

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const value = input.value.trim();
    lastSubmitCause = "submit";
    flushDebouncedInput();
    if (updateText && value) updateText(value);
    if (typeof standaloneSearchBox.submit === "function" && value) {
      standaloneSearchBox.submit();
    } else {
      navigateToSearch(value);
    }
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      flushDebouncedInput();
      if (wrapper) wrapper.classList.remove("coveo-suggestions-visible");
      if (suggestionsEl) suggestionsEl.classList.add("hidden");
      if (recentQueriesEl) recentQueriesEl.classList.add("hidden");
      input.blur();
    }
  });

  if (wrapper) {
    document.addEventListener("click", (e) => {
      if (!wrapper.contains(e.target)) {
        if (wrapper) wrapper.classList.remove("coveo-suggestions-visible");
        if (suggestionsEl) suggestionsEl.classList.add("hidden");
        if (recentQueriesEl) recentQueriesEl.classList.add("hidden");
      }
    });
  }

  if (updateText && input.value) {
    flushDebouncedInput();
    updateText(input.value);
  }
  if (typeof standaloneSearchBox.state === "object" && input.value && typeof standaloneSearchBox.showSuggestions === "function") {
    standaloneSearchBox.showSuggestions();
  }

  return standaloneSearchBox;
}

function renderSuggestions(container, suggestions, onSelect) {
  if (!container) return;
  if (!suggestions || suggestions.length === 0) {
    container.innerHTML = "";
    return;
  }
  container.innerHTML = suggestions
    .map((s) => {
      const text =
        s.highlightedValue || s.rawValue || (typeof s === "string" ? s : "");
      const raw = typeof s === "string" ? s : s.rawValue || text;
      return `<button type="button" class="coveo-headless-suggestion" data-value="${escapeHtml(raw)}">${escapeHtml(text)}</button>`;
    })
    .join("");

  container.querySelectorAll(".coveo-headless-suggestion").forEach((btn) => {
    btn.addEventListener("click", () => onSelect(btn.dataset.value || ""));
  });
}

window.initCoveoHeadlessSearchBox = async function (engine) {
  const wrapper = document.getElementById("coveo-search-wrapper");
  if (!wrapper) return null;

  const input =
    wrapper.querySelector("#coveo-search-input") ||
    wrapper.querySelector('input[name="q"]') ||
    document.getElementById("coveo-search-input");
  const form = input?.closest("form");
  const suggestionsEl =
    wrapper.querySelector("#coveo-search-suggestions") ||
    document.getElementById("coveo-search-suggestions");
  const recentQueriesEl = document.getElementById("coveo-recent-queries");

  if (!input || !form) return null;

  try {
    const commerceModule =
      await import("https://static.cloud.coveo.com/shopify/v1/headless/commerce.esm.js");
    const buildStandaloneSearchBox =
      commerceModule.buildStandaloneSearchBox ||
      commerceModule.standaloneSearchBox;

    if (!buildStandaloneSearchBox) {
      console.warn(
        "[Coveo Headless] buildStandaloneSearchBox not found. Using form fallback.",
      );
      form.setAttribute("action", window.FoxTheme?.routes?.search_url || "/search");
      form.setAttribute("method", "get");
      return null;
    }

    const box = wireSearchBoxWithSuggestions(engine, {
      input,
      form,
      suggestionsEl,
      recentQueriesEl,
      wrapper,
      buildStandaloneSearchBox,
      boxId: "header",
    });

    if (box) {
      window.CoveoHeadlessSearchBox = box;
    }
    return box;
  } catch (err) {
    console.error("[Coveo Headless] Search box init error:", err);
    form.setAttribute("action", window.FoxTheme?.routes?.search_url || "/search");
    form.setAttribute("method", "get");
    return null;
  }
};

window.initCoveoHeroSearchBox = async function (engine) {
  if (isShopifySearchTemplatePath()) return null;

  const heroForm = document.querySelector(".hero-search__form");
  const heroInput = heroForm?.querySelector(".hero-search__input");

  if (!heroForm || !heroInput) return null;

  const heroSuggestions = document.getElementById("hero-search-suggestions");

  try {
    const commerceModule =
      await import("https://static.cloud.coveo.com/shopify/v1/headless/commerce.esm.js");
    const buildStandaloneSearchBox =
      commerceModule.buildStandaloneSearchBox ||
      commerceModule.standaloneSearchBox;

    if (!buildStandaloneSearchBox) return null;

    return wireSearchBoxWithSuggestions(engine, {
      input: heroInput,
      form: heroForm,
      suggestionsEl: heroSuggestions,
      recentQueriesEl: null,
      wrapper: heroForm.closest(".hero-search"),
      buildStandaloneSearchBox,
      boxId: "hero",
    });
  } catch (err) {
    return null;
  }
};

function escapeHtml(str) {
  if (!str) return "";
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}
